// Blåtur strike rules, without any Firebase dependencies so they can be unit tested
//
// - A strike is an absence the organizers did not accept (they hear the reason first).
// - Strikes on two trips in a row and you are out. Attending, or a valid absence, breaks the row.
// - A readmission clears all strikes up to and including its `clearsUntil` trip year.

export const STRIKES_TO_OUT = 2;

const normalize = (name) => (name || '').trim().toLowerCase();

// tripYears: the years blåtur was held (or is planned), so "in a row" skips years without a trip
export function memberStatus(member, tripYears = []) {
  const clearedUntil = Math.max(0, ...(member.readmissions || []).map(r => r.clearsUntil || 0));
  const absences = member.absences || [];
  const strikeYears = new Set(absences.filter(a => a.strike && a.year > clearedUntil).map(a => a.year));
  const years = [...new Set([...tripYears, ...absences.map(a => a.year)])].sort((a, b) => a - b);

  let longestRow = 0;
  let row = 0;
  for (const year of years.filter(y => y > clearedUntil)) {
    row = strikeYears.has(year) ? row + 1 : 0;
    longestRow = Math.max(longestRow, row);
  }

  return {
    strikes: strikeYears.size,
    inARow: row, // strikes in a row up to the latest trip
    out: longestRow >= STRIKES_TO_OUT,
    strikeYears: [...strikeYears].sort((a, b) => a - b),
  };
}

// Absences on past trips for people who had already been on a blåtur before that trip.
// Used once to start practising the rule with the trips that have already happened.
export function historicalAbsences(members, trips, years) {
  const tripsByYear = new Map(trips.map(t => [parseInt(t.Kvartal, 10), t]));
  const firstTrip = {};
  trips.forEach(t => {
    const year = parseInt(t.Kvartal, 10);
    [...(t.Deltagelse || []), ...(t.Vert || [])].forEach(n => {
      const key = normalize(n);
      firstTrip[key] = Math.min(firstTrip[key] || Infinity, year);
    });
  });

  const result = [];
  for (const member of members) {
    const key = normalize(member.name);
    for (const year of years) {
      const trip = tripsByYear.get(year);
      if (!trip) continue;
      const attended = [...(trip.Deltagelse || []), ...(trip.Vert || [])].some(n => normalize(n) === key);
      const alreadyRegistered = (member.absences || []).some(a => a.year === year);
      const wasMember = firstTrip[key] < year;
      if (!attended && !alreadyRegistered && wasMember) result.push({ member, year, trip });
    }
  }
  return result;
}
